import { AxleFieldNames, AxleFormName } from './fields/AxleInformationFields';
import {
  TractorAxlesFormName,
  TractorFieldNames,
} from './fields/TractorInformationFields';
import { TrailerFieldNames } from './fields/TrailerInformationFields';
import { TrailerFormName, TripsFormName } from './OrderForm';

/**
 * Fields that should always be preserved regardless of permit fields.
 * These are ID fields that are used for entity relationships.
 */
const ID_FIELDS = [
  TrailerFieldNames.TrailerId,
  AxleFieldNames.AxleId,
  TractorFieldNames.TractorId,
] as const;

const ORDER_LEVEL_FIELDS = ['PONumber'] as const;

/**
 * Nested form field names that contain arrays or objects with their own fields.
 */
const NESTED_FORM_FIELDS = [
  AxleFormName,
  TrailerFormName,
  TractorAxlesFormName,
  TripsFormName,
];

const isIdField = (key: string): boolean => {
  return (ID_FIELDS as readonly string[]).includes(key);
};

const isOrderLevelField = (key: string): boolean => {
  return (ORDER_LEVEL_FIELDS as readonly string[]).includes(key);
};

/**
 * Filters form entries to only include fields that are in the permit's field list.
 * This prevents sending invalid data to the backend when permit types change.
 *
 * @param entries - The form entries to filter
 * @param fields - The list of valid fields for the current permit type(s)
 * @returns Filtered entries containing only valid fields
 */
export const filterFormEntries = (
  entries: Record<string, unknown>,
  fields: string[],
): Record<string, unknown> => {
  const fieldsSet = new Set(fields);

  const filterNestedArray = (items: unknown[]): unknown[] => {
    return items.map((item) => {
      if (typeof item === 'object' && item !== null) {
        const filteredItem: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(
          item as Record<string, unknown>,
        )) {
          if (fieldsSet.has(key) || isIdField(key)) {
            filteredItem[key] = val;
          } else if (key === AxleFormName && Array.isArray(val)) {
            filteredItem[key] = filterNestedArray(val);
          }
        }
        return filteredItem;
      }
      return item;
    });
  };

  const filterNestedObject = (
    obj: Record<string, unknown>,
  ): Record<string, unknown> => {
    const filteredObj: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      if (fieldsSet.has(key) || isIdField(key)) {
        filteredObj[key] = val;
      } else if (key === AxleFormName && Array.isArray(val)) {
        filteredObj[key] = filterNestedArray(val);
      }
    }
    return filteredObj;
  };

  /**
   * Filter trips object. The trips object has application IDs as keys
   * and trip data objects as values.
   */
  const filterTripsObject = (
    tripsObj: Record<string, unknown>,
  ): Record<string, unknown> => {
    const filteredTrips: Record<string, unknown> = {};
    for (const [appId, tripData] of Object.entries(tripsObj)) {
      if (typeof tripData === 'object' && tripData !== null) {
        const filteredTripData: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(
          tripData as Record<string, unknown>,
        )) {
          if (fieldsSet.has(key)) {
            filteredTripData[key] = val;
          }
        }
        // Only include the application entry if it has any fields left
        if (Object.keys(filteredTripData).length > 0) {
          filteredTrips[appId] = filteredTripData;
        }
      }
    }
    return filteredTrips;
  };

  return Object.fromEntries(
    Object.entries(entries)
      .filter(
        ([key]) =>
          fieldsSet.has(key) ||
          NESTED_FORM_FIELDS.includes(key) ||
          isOrderLevelField(key),
      )
      .map(([key, value]) => {
        if (key === TrailerFormName && Array.isArray(value)) {
          return [key, filterNestedArray(value)];
        }
        if (
          key === TractorAxlesFormName &&
          typeof value === 'object' &&
          value !== null
        ) {
          return [key, filterNestedObject(value as Record<string, unknown>)];
        }
        if (
          key === TripsFormName &&
          typeof value === 'object' &&
          value !== null
        ) {
          return [key, filterTripsObject(value as Record<string, unknown>)];
        }
        return [key, value];
      }),
  );
};
